import { FC, ReactNode, useMemo } from 'react';
import {
  FormControl,
  FormControlLabel,
  formControlLabelClasses,
  FormControlLabelProps,
  FormControlProps,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup as MuiRadioGroup,
  RadioGroupProps as MuiRadioGroupProps,
  RadioProps,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { v4 } from 'uuid';

export type RadioOption = Pick<FormControlLabelProps, 'label' | 'labelPlacement' | 'value'> &
  Pick<RadioProps, 'edge'> & {
    helperText?: string;
    label: ReactNode;
    value: boolean | number | string;
  };

export type RadioGroupProps = Pick<
  MuiRadioGroupProps,
  'onBlur' | 'onChange' | 'onFocus' | 'row' | 'value'
> &
  Pick<FormControlProps, 'disabled' | 'error' | 'margin' | 'required'> & {
    disabled?: boolean;
    helperText?: string;
    label: string;
    options: RadioOption[];
    required?: boolean;
  };

const StyledFormControl = styled(FormControl)`
  display: flex;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  align-items: flex-start;

  .${formControlLabelClasses.label} {
    margin-top: 8px;
  }
`;

export const RadioGroup: FC<RadioGroupProps> = ({
  disabled,
  error,
  helperText,
  label,
  margin,
  onBlur,
  onChange,
  onFocus,
  options,
  required,
  row,
  value = -1,
}) => {
  const labelId = useMemo(() => v4(), []);

  return (
    <StyledFormControl disabled={disabled} error={error} margin={margin} required={required}>
      <FormLabel id={labelId}>{label}</FormLabel>

      <MuiRadioGroup
        aria-labelledby={labelId}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        row={row}
        value={value}
      >
        {options.map(({ label, value, edge, helperText, labelPlacement }) => (
          <StyledFormControlLabel
            control={<Radio edge={edge} inputProps={{ required }} size='small' />}
            key={String(value)}
            label={
              helperText ? (
                <Stack>
                  {label}
                  <Typography color='text.secondary' mb={1} variant='caption'>
                    {helperText}
                  </Typography>
                </Stack>
              ) : (
                label
              )
            }
            labelPlacement={labelPlacement}
            value={value}
          />
        ))}
      </MuiRadioGroup>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </StyledFormControl>
  );
};
